import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap, tap } from 'rxjs';
import { ReferenceHttpService } from 'shared/domain';
import { tapAllResponses } from 'shared/util-common';

import { ExtractReferenceResult, Reference } from '@treetop/shared/model';

type SelectableReference = {
  reference: Reference;
  selected: boolean;
  disabled?: boolean;
};

type ReferencePopoverState = {
  newReferences: Reference[];
  existingReferences: Reference[];
  loading: boolean;
  selected: Reference[];
  newlySelected: Reference[];
  disabled: Reference[];
  file: File | undefined;
  error: string | undefined;
  mode: 'file' | 'search';
  searchValue: string;
  disableExisting: boolean;
};

export type ReferencePopoverViewModel = {
  newReferences: SelectableReference[];
  existingReferences: SelectableReference[];
  loading: boolean;
  selected: Reference[];
  newlySelectedCount: number;
  file: File | undefined;
  error: string | undefined;
  mode: 'file' | 'search';
  searchValue: string;
  disableExisting: boolean;
};

const initialState: ReferencePopoverState = {
  newReferences: [],
  existingReferences: [],
  loading: false,
  selected: [],
  newlySelected: [],
  disabled: [],
  file: undefined,
  error: undefined,
  mode: 'search',
  searchValue: '',
  disableExisting: false,
};

export type ReferencePopoverStateInitOptions = {
  disabled?: Reference[];
  selected?: Reference[];
  disableExisting?: boolean;
  mode?: 'search';
};

export const ReferencePopoverStore = signalStore(
  withState(initialState),
  withComputed((store) => {
    return {
      vm: computed<ReferencePopoverViewModel>(() => {
        const selected = [...store.selected(), ...store.newlySelected()];
        return {
          newReferences: store
            .newReferences()
            .map((reference) => ({
              reference,
              selected: selected.some((selected) => selected.rawContentHash === reference.rawContentHash),
              disabled: store.disabled().some((disabled) => disabled.rawContentHash === reference.rawContentHash),
            }))
            .sort((a, b) => Number(a.disabled) - Number(b.disabled)),
          existingReferences: store
            .existingReferences()
            .map((reference) => ({
              reference,
              selected: selected.some((selected) => selected.rawContentHash === reference.rawContentHash),
              disabled: store.disabled().some((disabled) => disabled.rawContentHash === reference.rawContentHash),
            }))
            .sort((a, b) => Number(a.disabled) - Number(b.disabled)),
          selected,
          newlySelectedCount: store.newlySelected().length,
          loading: store.loading(),
          file: store.file(),
          error: store.error(),
          mode: store.mode(),
          searchValue: store.searchValue(),
          disableExisting: store.disableExisting(),
        };
      }),
    };
  }),
  withMethods((store) => {
    const referenceService = inject(ReferenceHttpService);

    const init = (options?: ReferencePopoverStateInitOptions) => {
      patchState(store, {
        disabled: options?.disabled ?? [],
        selected: options?.selected ?? [],
        disableExisting: options?.disableExisting,
      });
    };

    const selectDeselectReference = (reference: Reference) => {
      const newlySelected = store.newlySelected();
      const index = newlySelected.findIndex((selected) => selected.rawContentHash === reference.rawContentHash);

      if (index !== -1) {
        newlySelected.splice(index, 1);
      } else {
        newlySelected.push(reference);
      }

      patchState(store, { newlySelected: [...newlySelected] });
    };

    const extractOrSearch = rxMethod<string>((search$) =>
      search$.pipe(
        tap((search) => {
          patchState(store, { loading: true, searchValue: search });
        }),
        switchMap((search) => referenceService.extractOrSearch(search)),
        tapAllResponses<ExtractReferenceResult, HttpErrorResponse>(
          ({ existingReferences, newReferences }) => {
            patchState(store, {
              existingReferences,
              newReferences,
              loading: false,
              newlySelected: [],
              error: undefined,
            });
          },
          (error) => {
            patchState(store, {
              loading: false,
              existingReferences: [],
              newReferences: [],
              newlySelected: [],
              error: error.error?.Message ?? 'Error',
            });
          },
        ),
      ),
    );

    const extractFromFile = rxMethod<File>((file$) =>
      file$.pipe(
        tap(() => {
          patchState(store, { loading: true });
        }),
        switchMap((file$) => referenceService.extractFromFile(file$)),
        tapAllResponses<ExtractReferenceResult, HttpErrorResponse>(
          ({ existingReferences, newReferences }) => {
            patchState(store, {
              existingReferences,
              newReferences,
              loading: false,
              newlySelected: [],
              error: undefined,
            });
          },
          (error) => {
            patchState(store, {
              loading: false,
              existingReferences: [],
              newReferences: [],
              newlySelected: [],
              error: error.error?.Message ?? 'Error',
            });
          },
        ),
      ),
    );

    const updateFile = (file: File) => {
      if (!file) {
        patchState(store, { file, newReferences: [], existingReferences: [], newlySelected: [], error: undefined });
        return;
      }

      patchState(store, { file, error: undefined });

      extractFromFile(file);
    };

    const switchMode = (mode: 'file' | 'search') => {
      patchState(store, { newReferences: [], existingReferences: [], newlySelected: [], error: undefined, mode, searchValue: '' });
    };

    return {
      init,
      selectDeselectReference,
      updateFile,
      switchMode,
      extractOrSearch,
    };
  }),
);
